import React, { useState, useEffect } from 'react';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { IPageViewTelemetry } from '@microsoft/applicationinsights-web';
import Heading from '@athena/spark/Heading';
import { appInsights } from 'src/shared/lib/appInsights';
import { useAmplitude } from 'src/lib/amplitude';
import {
  ubsToBrowser,
  UnsupportedBrowserScenario,
  isUnsupportedVersion,
  isUnsupportedBrowserOfOS,
} from 'src/components/utils/Helper';
import BrowserDownloadButton from 'src/components/ui/BrowserDownloadButton';
import { useAppointmentState } from 'src/components/provider/AppointmentProvider';
import { AmplitudeButton } from 'src/components/container/AmplitudeButton';
import styles from './BrowserWarning.module.scss';

export interface BrowserWarningProps {
  unsupportedBrowserKey: UnsupportedBrowserScenario;
  workflow: 'DEVICECHECK' | 'APPOINTMENT';
}

const copyLink = (
  copyTimeoutId: number | null,
  setCopyTimeoutId: React.Dispatch<React.SetStateAction<number | null>>
) => {
  const link = window.location.href;
  window.navigator.clipboard.writeText(link);
  if (copyTimeoutId) {
    clearTimeout(copyTimeoutId);
  }
  const timeoutId = window.setTimeout(() => {
    setCopyTimeoutId(null);
  }, 5000);
  setCopyTimeoutId(timeoutId);
};

const getPrimaryCopy = (scenario: UnsupportedBrowserScenario) =>
  isUnsupportedBrowserOfOS(scenario) ? 'UNSUPPORTED_BROWSER_PRIMARY_COPY' : `${scenario}_PRIMARY_COPY`;

const getDownloadCopy = (scenario: UnsupportedBrowserScenario) =>
  scenario === UnsupportedBrowserScenario.INTERNET_EXPLORER
    ? 'DOWNLOAD_SUPPORTED_BROWSER_IF_NEEDED'
    : 'DOWNLOAD_SUPPORTED_BROWSER';

const downloadOptionScenarios: any = {
  [UnsupportedBrowserScenario.INTERNET_EXPLORER]: ['Google Chrome', 'Microsoft Edge'],
  [UnsupportedBrowserScenario.UNSUPPORTED_WINDOWS_BROWSER]: ['Google Chrome', 'Microsoft Edge'],
  [UnsupportedBrowserScenario.UNSUPPORTED_MACOS_BROWSER]: ['Google Chrome', 'Safari'],
  [UnsupportedBrowserScenario.UNSUPPORTED_IOS_BROWSER]: ['Safari'],
  [UnsupportedBrowserScenario.UNSUPPORTED_ANDROID_BROWSER]: ['Google Chrome'],
};

const BrowserWarning: React.FC<BrowserWarningProps> = ({ unsupportedBrowserKey, workflow }) => {
  const [copyTimeoutId, setCopyTimeoutId] = useState<number | null>(null);
  const { t } = useTranslation();
  const pageName = isUnsupportedVersion(unsupportedBrowserKey) ? 'UnsupportedBrowserVersion' : 'UnsupportedBrowser';
  const { ampTrackPageView, getProductName } = useAmplitude(pageName);
  const { appointmentId } = useAppointmentState();

  useEffect(() => {
    const browserProps = {
      UserAgent: navigator.userAgent,
    };

    if (getProductName() === 'THDeviceCheckApp' || appointmentId) {
      // this prevents ampTrackPageView from being called twice in the THWebApp
      // where it calls it once before the appointmentId is known and once after
      // while still ensuring it is called once in the THDeviceCheckApp
      ampTrackPageView();

      appInsights.trackPageView({
        name: pageName,
        properties: browserProps,
      } as IPageViewTelemetry);
    }

    // We'd like to wait untill the amplitude session id is generated
    // and appointment details are loaded before logging
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointmentId]);

  const copyLinkSupportedByBrowser = !!window.navigator.clipboard;

  return (
    <main className={styles.appWarning}>
      <div className={cx(styles.contentContainer, styles.paddedTop)}>
        <div className={styles.computerImage} />
        <div className={styles.copyContainer}>
          <Heading className={styles.heading} variant="section">
            {t(getPrimaryCopy(unsupportedBrowserKey))}
          </Heading>

          <p className={styles.subHeading}>
            {t(`${unsupportedBrowserKey}_SECONDARY_COPY`)} {t(`THEN_CTA_${workflow}`)}
          </p>

          {!isUnsupportedVersion(unsupportedBrowserKey) &&
          unsupportedBrowserKey !== UnsupportedBrowserScenario.INTERNET_EXPLORER &&
          copyLinkSupportedByBrowser ? (
            <>
              <AmplitudeButton
                action="CopyLink"
                pageName={pageName}
                variant="solid-dark"
                onClick={() => copyLink(copyTimeoutId, setCopyTimeoutId)}
              >
                <span>{t(`COPY_${workflow}_LINK`)}</span>
              </AmplitudeButton>
              <p className={cx(styles.copyMsg, { [styles.invisible]: !copyTimeoutId })}>
                {t(`${workflow}_LINK_COPIED`)}
              </p>
            </>
          ) : null}
          {unsupportedBrowserKey in downloadOptionScenarios ? (
            <div className={styles.downloadBrowser}>
              <p className={cx(styles.downloadCopy, 'fe_u_text-align--center')}>
                <strong>{t(getDownloadCopy(unsupportedBrowserKey))}</strong>
              </p>
              <div className={styles.browserOptions}>
                {downloadOptionScenarios[unsupportedBrowserKey].map((browser: string) => (
                  <BrowserDownloadButton pageName={pageName} browserKey={browser} key={browser} />
                ))}
              </div>
            </div>
          ) : null}
          {isUnsupportedVersion(unsupportedBrowserKey) ? (
            <div className={cx(styles.downloadBrowser, styles.noTopBorder)}>
              <BrowserDownloadButton pageName={pageName} browserKey={ubsToBrowser(unsupportedBrowserKey)} />
            </div>
          ) : null}
        </div>
      </div>
    </main>
  );
};

export default BrowserWarning;
